import { workerHttpUrl } from './worker-config';

// C010 (launch operations): a client for the two moderation-only,
// `ADMIN_TOKEN`-gated read/write surfaces (`admin-routes.ts`,
// `store-admin-routes.ts`). Deliberately a separate auth scheme from the
// player-facing `authorizedFetch` in session.ts — this never carries a
// player's session token, only the moderator's admin token, sent as
// `X-Admin-Token`. Unset on the server ⇒ every call here 404s (the surface
// doesn't exist); wrong token ⇒ 403.

const ADMIN_TOKEN_KEY = 'rw_admin_token';

export function getAdminToken(): string {
  return localStorage.getItem(ADMIN_TOKEN_KEY) ?? '';
}

export function setAdminToken(token: string): void {
  if (token) localStorage.setItem(ADMIN_TOKEN_KEY, token);
  else localStorage.removeItem(ADMIN_TOKEN_KEY);
}

async function adminFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(options.headers as Record<string, string>) };
  const token = getAdminToken();
  if (token) headers['X-Admin-Token'] = token;
  const response = await fetch(`${workerHttpUrl()}${path}`, { ...options, headers });
  if (response.status === 404) throw new Error('Not found — the admin surface may be disabled on this deploy (ADMIN_TOKEN unset).');
  if (response.status === 403) throw new Error('Forbidden — check the admin token above.');
  const data = (await response.json()) as T & { error?: string };
  if (!response.ok) throw new Error(data.error ?? `request failed with status ${response.status}`);
  return data;
}

export interface IntegritySignal {
  name: string;
  fired: boolean;
  metric: number;
  threshold: number;
  description: string;
}

export interface IntegrityAssessment {
  sampleSize: number;
  verdict: 'insufficient-data' | 'clean' | 'watch' | 'review';
  signals: IntegritySignal[];
  note: string;
}

/** A moderator has a username on hand, not the internal id, so this always looks up by `username` (the server also accepts `userId` directly, for the worker test suite). */
export function fetchIntegrity(username: string): Promise<{ userId: string; username: string; assessment: IntegrityAssessment }> {
  const params = new URLSearchParams({ username });
  return adminFetch(`/api/admin/integrity?${params.toString()}`);
}

export interface ReconciliationDrift {
  sessionId: string;
  userId: string;
  kind: string;
  issue: 'missing_entitlement' | 'missing_credit';
}

export interface StuckPending {
  sessionId: string;
  userId: string;
  ageMs: number;
}

export interface ReconciliationReport {
  generatedAt: number;
  completedChecked: number;
  pendingChecked: number;
  drift: ReconciliationDrift[];
  stuckPending: StuckPending[];
  clean: boolean;
}

export function reconcileStore(): Promise<ReconciliationReport> {
  return adminFetch('/api/admin/store/reconcile');
}

export interface RefundResult {
  ok: true;
  sessionId: string;
  refunded: boolean;
  kind: string;
}

export function refundPurchase(sessionId: string): Promise<RefundResult> {
  return adminFetch('/api/admin/store/refund', { method: 'POST', body: JSON.stringify({ sessionId }) });
}

export interface AnalyticsReport {
  generatedAt: number;
  windowDays: number;
  growth: {
    totalUsers: number;
    registrationsByDay: { day: string; count: number }[];
  };
  activity: {
    byDay: { day: string; matches: number; rankedMatches: number; activePlayers: number }[];
    active7d: number;
    active30d: number;
    totalMatches: number;
    totalRankedMatches: number;
  };
  progression: {
    levelDistribution: { level: number; count: number }[];
    achievements: {
      totalUnlocks: number;
      playersWithAny: number;
      topUnlocked: { achievementId: string; name: string; count: number }[];
      rarestUnlocked: { achievementId: string; name: string; count: number }[];
    };
    xp: { totalGranted: number; byReason: { reason: string; total: number; grants: number }[] };
  };
  economy: {
    currency: {
      byReason: { currency: string; reason: string; net: number; entries: number }[];
      outstandingBalances: { currency: string; balance: number }[];
    };
    items: { byReason: { reason: string; count: number }[]; distinctItemsGranted: number };
    wallet: { kind: string; status: string; count: number; cents: number }[];
  };
  social: {
    friendships: { status: string; count: number }[];
    totalBlocks: number;
    challenges: { status: string; count: number }[];
  };
}

/** `days` sizes the growth/activity time series (default 14, capped at 90 server-side). Everything else is all-time. */
export function fetchAnalytics(days = 14): Promise<AnalyticsReport> {
  return adminFetch(`/api/admin/analytics?days=${days}`);
}
